import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { REVIEWS, type Review } from "@/data/reviews";
import { DragCarousel } from "./DragCarousel";
import { ExpandingSection } from "./ExpandingSection";

const PREVIEW_CHARS = 280;

function Initials({ name }: { name: string }) {
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      aria-hidden="true"
      className="h-12 w-12 shrink-0 border border-white/15 bg-[#0a0d0b] flex items-center justify-center font-mono text-[11px] tracking-[0.2em] text-white/70"
    >
      {initials}
    </div>
  );
}

function truncateBySentence(text: string, max: number) {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  // Try to cut at the last sentence boundary; fall back to last whitespace
  const lastSentence = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("! "), slice.lastIndexOf("? "));
  if (lastSentence > max * 0.5) return text.slice(0, lastSentence + 1) + " …";
  const lastSpace = slice.lastIndexOf(" ");
  return text.slice(0, lastSpace > 0 ? lastSpace : max).trimEnd() + " …";
}

function ReviewCard({ review }: { review: Review }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const bodyId = useId();

  const isLong = review.quote.length > PREVIEW_CHARS;
  const preview = truncateBySentence(review.quote, PREVIEW_CHARS);
  const blockquoteClass =
    "text-body text-base md:text-[17px] leading-relaxed text-white/85 whitespace-pre-line";

  return (
    <article className="group flex flex-col h-full border border-white/10 bg-[#0a0d0b] p-7 md:p-8 hover:border-lime-300/40 transition-colors">
      <span aria-hidden="true" className="font-display text-5xl leading-none text-lime-300/70 mb-4">
        “
      </span>
      {isLong ? (
        <div className="flex-1">
          {!expanded && <blockquote className={blockquoteClass}>{preview}</blockquote>}
          <ExpandingSection open={expanded}>
            <blockquote id={bodyId} className={blockquoteClass}>
              {review.quote}
            </blockquote>
          </ExpandingSection>
        </div>
      ) : (
        <blockquote className={`${blockquoteClass} flex-1`}>{review.quote}</blockquote>
      )}

      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 self-start py-2 min-h-[44px] inline-flex items-center font-mono text-[11px] uppercase tracking-[0.28em] text-lime-300 hover:text-lime-200 transition-colors"
          aria-expanded={expanded}
          aria-controls={bodyId}
        >
          {expanded ? t("reviews.showLess") : t("reviews.readMore")}
        </button>
      )}

      <footer className="mt-7 pt-6 border-t border-white/10 flex items-center gap-4">
        {review.avatar && !imgFailed ? (
          <img
            src={review.avatar}
            alt={`${review.author} avatar`}
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
            className="h-12 w-12 shrink-0 object-cover border border-white/15 bg-[#0a0d0b]"
            style={{ filter: "grayscale(100%) contrast(1.05)" }}
          />
        ) : (
          <Initials name={review.author} />
        )}
        <div className="min-w-0">
          <p className="font-display text-white text-base leading-tight truncate">
            {review.author}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">
            {t("reviews.verified")}
          </p>
        </div>
      </footer>
    </article>
  );
}

export default function ReviewsCarousel() {
  const { t } = useTranslation();
  return (
    <div role="region" aria-roledescription="carousel" aria-label={t("reviews.regionLabel")}>
      <DragCarousel>
        {REVIEWS.map((r, i) => (
          <div
            key={`${r.author}-${i}`}
            className="w-[340px] sm:w-[380px]"
            role="group"
            aria-roledescription="slide"
            aria-label={t("reviews.slideOf", { n: i + 1, total: REVIEWS.length })}
          >
            <ReviewCard review={r} />
          </div>
        ))}
      </DragCarousel>
    </div>
  );
}
